import {
  WebSocketConnectionState,
  WebSocketEvent,
  WebSocketMessage,
} from '@fly/shared';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

interface WebSocketState {
  connections: Record<string, WebSocketConnectionState>;
  messages: WebSocketMessage[];
  events: WebSocketEvent[];
  maxMessages: number;
  maxEvents: number;
}

const initialState: WebSocketState = {
  connections: {},
  messages: [],
  events: [],
  maxMessages: 1000, // Keep last 1000 messages
  maxEvents: 100, // Keep last 100 events
};

export const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    // Connection management
    setConnectionState: (
      state,
      action: PayloadAction<{
        connectionId: string;
        connectionState: Partial<WebSocketConnectionState>;
      }>
    ) => {
      const { connectionId, connectionState } = action.payload;

      // Initialize connection if it doesn't exist
      if (!state.connections[connectionId]) {
        state.connections[connectionId] = {
          isConnected: false,
          isConnecting: false,
          isReconnecting: false,
          reconnectAttempts: 0,
        };
      }

      // Update with new state
      Object.assign(state.connections[connectionId], connectionState);
    },

    removeConnection: (state, action: PayloadAction<string>) => {
      delete state.connections[action.payload];
    },

    // Message management
    addMessage: (state, action: PayloadAction<WebSocketMessage>) => {
      state.messages.push(action.payload);

      // Keep only the last maxMessages
      if (state.messages.length > state.maxMessages) {
        state.messages = state.messages.slice(-state.maxMessages);
      }
    },

    addMessages: (state, action: PayloadAction<WebSocketMessage[]>) => {
      state.messages.push(...action.payload);

      // Keep only the last maxMessages
      if (state.messages.length > state.maxMessages) {
        state.messages = state.messages.slice(-state.maxMessages);
      }
    },

    clearMessages: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload) {
        // Clear messages for specific connection
        state.messages = state.messages.filter(
          (msg) => msg.source !== action.payload
        );
      } else {
        // Clear all messages
        state.messages = [];
      }
    },

    // Event management
    addEvent: (state, action: PayloadAction<WebSocketEvent>) => {
      state.events.push(action.payload);

      // Keep only the last maxEvents
      if (state.events.length > state.maxEvents) {
        state.events = state.events.slice(-state.maxEvents);
      }
    },

    clearEvents: (state) => {
      state.events = [];
    },

    // Configuration
    setMaxMessages: (state, action: PayloadAction<number>) => {
      state.maxMessages = action.payload;

      // Trim messages if needed
      if (state.messages.length > state.maxMessages) {
        state.messages = state.messages.slice(-state.maxMessages);
      }
    },

    setMaxEvents: (state, action: PayloadAction<number>) => {
      state.maxEvents = action.payload;

      // Trim events if needed
      if (state.events.length > state.maxEvents) {
        state.events = state.events.slice(-state.maxEvents);
      }
    },
  },
});

export const {
  setConnectionState,
  removeConnection,
  addMessage,
  clearMessages,
  addEvent,
  clearEvents,
  setMaxMessages,
  setMaxEvents,
} = websocketSlice.actions;

export default websocketSlice.reducer;
